/*
 * GitLab платформы (Resource gitlab): сводка бэкенд-плагина viva-gitlab (GET /api/viva-gitlab/summary
 * — версия, редакция, KAS, статус обновлений по version.gitlab.com) и её подписи для страницы ресурса
 * и уведомления на главной. Состав инстанса и состояние — из метрик кластера, который их собирает
 * (компонент gitlab-metrics, метка gitlab="platform").
 */
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export type UpdateStatus =
  | 'up-to-date'
  | 'update-available'
  | 'update-asap'
  | 'unknown'
  | 'disabled';

export type GitlabSummary = {
  url: string;
  version?: string;
  revision?: string;
  edition?: 'CE' | 'EE';
  kas?: {
    enabled: boolean;
    version?: string;
    externalUrl?: string;
    externalK8sProxyUrl?: string;
  };
  error?: string;
  update: {
    status: UpdateStatus;
    critical?: boolean;
    latestVersion?: string;
    latestOfMinor?: string;
    latestStable?: string[];
    checkedAt?: string;
    error?: string;
  };
};

export async function fetchGitlabSummary(apis: {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}): Promise<GitlabSummary> {
  const base = await apis.discoveryApi.getBaseUrl('viva-gitlab');
  const response = await apis.fetchApi.fetch(`${base}/summary`);
  if (!response.ok) {
    throw new Error(`Сводка GitLab: HTTP ${response.status}`);
  }
  return response.json();
}

/** Сравнение версий x.y.z (без суффиксов): отрицательное — a старше b. */
export const compareVersions = (a: string, b: string): number => {
  const pa = a.split('.').map(n => parseInt(n, 10) || 0);
  const pb = b.split('.').map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
};

/**
 * Версия, до которой обновляться: последняя в текущей минорной ветке (минимальное безопасное
 * обновление, только патчи), если она новее текущей; иначе — последняя вообще.
 */
export const updateTarget = (s: GitlabSummary): string | undefined => {
  const { latestOfMinor, latestVersion } = s.update ?? {};
  if (s.version && latestOfMinor && compareVersions(latestOfMinor, s.version) > 0) {
    return latestOfMinor;
  }
  return latestVersion;
};

export type UpdateTone = 'ok' | 'warning' | 'error' | 'pending';
export type UpdateView = { tone: UpdateTone; title: string; detail?: string };

export const updateView = (s: GitlabSummary): UpdateView => {
  const u = s.update;
  const target = updateTarget(s);
  switch (u?.status) {
    case 'up-to-date':
      return { tone: 'ok', title: 'Версия актуальна' };
    case 'update-available':
      return {
        tone: u.critical ? 'error' : 'warning',
        title: target ? `Доступно обновление до ${target}` : 'Доступно обновление',
        detail: u.critical ? 'В текущей версии критическая уязвимость' : undefined,
      };
    case 'update-asap':
      return {
        tone: 'error',
        title: u.critical
          ? `Критическое обновление безопасности до ${target ?? 'новой версии'}`
          : `Нужно срочно обновиться до ${target ?? 'новой версии'}`,
        detail: u.critical
          ? 'В текущей версии критическая уязвимость — GitLab рекомендует обновиться как можно скорее'
          : 'GitLab рекомендует обновиться как можно скорее',
      };
    case 'disabled':
      return {
        tone: 'pending',
        title: 'Проверка обновлений выключена',
        detail: 'viva.gitlabVersionCheck: false в конфигурации Backstage',
      };
    default:
      return {
        tone: 'pending',
        title: 'Проверить обновления не удалось',
        detail: u?.error,
      };
  }
};

/** Уведомлять на главной: обновиться нужно срочно или в версии критическая уязвимость. */
export const needsAttention = (s: GitlabSummary): boolean =>
  s.update?.status === 'update-asap' || Boolean(s.update?.critical);

/** Инструмент GitLab «Upgrade Path»: промежуточные версии от текущей до последней. */
export const upgradePathUrl = (s: GitlabSummary): string =>
  `https://gitlab-com.gitlab.io/support/toolbox/upgrade-path/?current=${encodeURIComponent(
    s.version ?? '',
  )}&distro=docker&edition=${(s.edition ?? 'CE').toLowerCase()}`;

export const RELEASES_URL = 'https://about.gitlab.com/releases/categories/releases/';

const G = 'gitlab="platform"';
/** Мгновенные запросы состава и состояния (Prometheus кластера с компонентом gitlab-metrics). */
export const GITLAB_STAT_QUERIES = {
  users: `sum(gitlab_database_rows{${G},query_name="users",state="active"})`,
  projects: `sum(gitlab_database_rows{${G},query_name="projects",archived="false"})`,
  groups: `sum(gitlab_database_rows{${G},query_name="namespaces",type="Group"})`,
  images: `sum(gitlab_database_rows{${G},query_name="container_repositories"})`,
  exportersUp: `sum(up{job="gitlab",${G}})`,
  exporters: `count(up{job="gitlab",${G}})`,
  available: `min(probe_success{app="gitlab",${G}})`,
  deadJobs: `max(sidekiq_jobs_dead_size{${G}})`,
  queueLatency: `max(sidekiq_queue_latency_seconds{${G}})`,
} as const;

/** Допустимые цели обновления (viva-gitlab /upgrade-options): шаг за шагом через остановки. */
export type UpgradeOptions = {
  current?: string;
  edition?: string;
  latest?: string;
  nextStop?: string;
  /** Патч текущей минорной ветки, если он новее текущей версии. */
  patch?: string;
  /** Следующий шаг пути — цель кнопки «Обновить». */
  next?: string;
  path: string[];
  /** На установке есть viva-ops (GitLab на бутстрап-ноде обновляет платформа). */
  managed: boolean;
  error?: string;
};

export type UpgradeRun = {
  id: string;
  from: string;
  to: string;
  state: 'running' | 'succeeded' | 'failed' | 'interrupted';
  dryRun?: boolean;
  backup?: boolean;
  requestedBy?: string;
  createdAt?: string;
  finishedAt?: string;
  message?: string;
  backupPath?: string;
};

export type RunnerInfo = {
  id: number;
  description?: string;
  status?: string;
  versions: string[];
};

export type UpgradesState = {
  available: boolean;
  running?: UpgradeRun | null;
  history?: UpgradeRun[];
  /** Раннеры инстанса с версиями (admin API через viva-ops). */
  runners?: RunnerInfo[];
  error?: string;
};

/**
 * Раннер отстаёт: его ветка major.minor старше ветки GitLab больше чем на одну минорную версию
 * (или другой major) — раннеры в кластерах обновляются отдельно (gitops-чарт раннера).
 */
export const runnerLags = (gitlabVersion: string | undefined, runnerVersion: string): boolean => {
  const g = /^(\d+)\.(\d+)/.exec(gitlabVersion ?? '');
  const r = /^v?(\d+)\.(\d+)/.exec(runnerVersion);
  if (!g || !r) return false;
  const [gMajor, gMinor, rMajor, rMinor] = [g[1], g[2], r[1], r[2]].map(Number);
  return rMajor !== gMajor || gMinor - rMinor > 1;
};

const getJson = async <T,>(
  apis: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  path: string,
): Promise<T> => {
  const base = await apis.discoveryApi.getBaseUrl('viva-gitlab');
  const response = await apis.fetchApi.fetch(`${base}${path}`);
  if (!response.ok && response.status !== 502) {
    throw new Error(`viva-gitlab ${path}: HTTP ${response.status}`);
  }
  return response.json();
};

export const fetchUpgradeOptions = (apis: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) =>
  getJson<UpgradeOptions>(apis, '/upgrade-options');

export const fetchUpgrades = (apis: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) =>
  getJson<UpgradesState>(apis, '/upgrades');

/** Шаблон «Обновление GitLab платформы» с подставленной версией. */
export const upgradeTemplateUrl = (version: string): string =>
  `/create/templates/default/gitlab-upgrade?formData=${encodeURIComponent(JSON.stringify({ version }))}`;

/** Группа администраторов платформы: только она видит кнопку обновления (проверяет и бэкенд). */
export const PLATFORM_ADMIN_GROUP = 'group:default/viva';

export const RUN_STATE_LABELS: Record<UpgradeRun['state'], string> = {
  running: 'идёт',
  succeeded: 'успешно',
  failed: 'ошибка',
  interrupted: 'прервано',
};
